<template>
  <v-text-field
    ref="docInput"
    :model-value="numero"
    variant="outlined"
    density="compact"
    hide-details="auto"
    label="Número de documento"
    :autocomplete="nativeAutocomplete"
    :name="nativeName"
    :disabled="disabled"
    :readonly="readonly || disabled"
    :rules="combinedRules"
    :maxlength="documentoMaxLength"
    :active="fieldHasValue || forceFloatingLabel"
    :dirty="fieldHasValue || forceFloatingLabel"
    class="tipo-doc-field"
    :class="{ 'tipo-doc-field--dark': isDarkTheme }"
    @update:model-value="onUpdateModel"
    @blur="onBlur"
    @keydown="onKeydown"
    @paste="onPaste"
  >
    <template #prepend-inner>
      <div class="tipo-doc-prepend d-flex align-center">
        <span
          v-if="readonly || disabled || loadingTipos"
          class="tipo-doc-tipo-fijo text-body-1 text-high-emphasis px-2 text-primary"
        >
          {{ tipoLabel }}
        </span>
        <v-menu v-else v-model="menuOpen" location="bottom">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="text"
              class="btn-tipo-doc text-body-1 px-2"
              height="auto"
              min-width="0"
              :disabled="!tiposDoc.length"
            >
              {{ tipoLabel }}
            </v-btn>
          </template>
          <v-list density="compact" max-height="320">
            <v-list-item
              v-for="item in tiposDoc"
              :key="item.id"
              :title="item.value || ''"
              @click="selectTipo(item)"
            />
          </v-list>
        </v-menu>
        <v-divider vertical class="tipo-doc-divider mx-2" />
      </div>
    </template>
  </v-text-field>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import rules from '../../utils/helpers/rules'

function normalizeMascara(mascara) {
  if (!mascara || typeof mascara !== 'string') return ''
  return mascara.trim()
}

function isMaskToken(char) {
  return ['9', '#', 'X', 'x', 'A', 'a', 'N', 'n'].includes(char)
}

function charMatchesMaskToken(char, token) {
  if (['9', '#'].includes(token)) return /\d/.test(char)
  if (['A', 'a'].includes(token)) return /[a-zA-Z]/.test(char)
  if (['N', 'n'].includes(token)) return /[a-zA-Z0-9]/.test(char)
  if (['X', 'x'].includes(token)) return /^[\x20-\x7E]$/.test(char)
  return false
}

function mascaraIsOnlyDigits(mascara) {
  const mask = normalizeMascara(mascara)
  const tokens = [...mask].filter(isMaskToken)
  return tokens.length > 0 && tokens.every((token) => ['9', '#'].includes(token))
}

function getMascaraFromTipo(tipo) {
  return normalizeMascara(
    tipo?.mascara
      ?? tipo?.Mascara
      ?? tipo?.mask
      ?? tipo?.Mask
  )
}

function canCompleteMascaraFrom(maskChars, rawChars, maskIndex, rawIndex) {
  for (let i = maskIndex; i < maskChars.length; i += 1) {
    const maskChar = maskChars[i]
    if (isMaskToken(maskChar)) {
      return rawIndex < rawChars.length && charMatchesMaskToken(rawChars[rawIndex], maskChar)
    }
    if (rawChars[rawIndex] === maskChar) rawIndex += 1
  }
  return false
}

function formatWithMascara(mascara, value) {
  const mask = normalizeMascara(mascara)
  if (!mask || value == null) return value == null ? '' : String(value)

  const rawChars = [...String(value).replace(/[^\x20-\x7E]/g, '')]
  const maskChars = [...mask]
  let rawIndex = 0
  let result = ''

  for (let maskIndex = 0; maskIndex < maskChars.length; maskIndex += 1) {
    const maskChar = maskChars[maskIndex]
    if (!isMaskToken(maskChar)) {
      if (rawChars[rawIndex] === maskChar) rawIndex += 1
      if (canCompleteMascaraFrom(maskChars, rawChars, maskIndex + 1, rawIndex)) {
        result += maskChar
      }
      continue
    }

    if (rawIndex >= rawChars.length) break
    const char = rawChars[rawIndex]
    if (!charMatchesMaskToken(char, maskChar)) break
    result += char
    rawIndex += 1
  }

  return result
}

export default {
  name: 'TipoDocumentoTextField',
  props: {
    tipoId: {
      default: null,
      validator: (v) =>
        v === null || v === undefined || (typeof v === 'number' && Number.isInteger(v)),
    },
    numero: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    /** Solo lectura: el usuario no puede cambiar el número ni el tipo. */
    readonly: {
      type: Boolean,
      default: false,
    },
    /** Reglas extra para el número (p. ej. rules.required) */
    rulesNroDoc: {
      type: Array,
      default: () => [],
    },
    /** En login usar `username` para que el gestor de contraseñas relacione documento + clave. */
    nativeAutocomplete: {
      type: String,
      default: 'off',
    },
    nativeName: {
      type: String,
      default: 'documento',
    },
    forceFloatingLabel: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:tipoId', 'update:numero', 'tipo-change'],
  data() {
    return {
      menuOpen: false,
      initialized: false,
      docNativeHasValue: false,
      docAutofillTimeouts: [],
      docAutofillOnAnim: null,
    }
  },
  computed: {
    ...mapState('taxonomy', ['tiposDocumento', 'hasFetched']),
    tiposDoc() {
      return this.tiposDocumento || []
    },
    loadingTipos() {
      return !this.hasFetched
    },
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name
      const value = themeName && typeof themeName === 'object'
        ? themeName.value
        : themeName
      return value === 'dark'
    },
    selectedTipo() {
      if (this.tipoId == null) return null
      return this.tiposDoc.find((t) => t.id === this.tipoId) || null
    },
    tipoLabel() {
      return this.selectedTipo?.value || '…'
    },
    selectedMascara() {
      return getMascaraFromTipo(this.selectedTipo)
    },
    hasMascara() {
      return Boolean(this.selectedMascara)
    },
    documentoMaxLength() {
      if (this.hasMascara) return this.selectedMascara.length
      return 20
    },
    combinedRules() {
      const extra = Array.isArray(this.rulesNroDoc) ? [...this.rulesNroDoc] : []
      extra.push(rules.maxLength(this.documentoMaxLength))
      return extra
    },
    fieldHasValue() {
      return Boolean(this.numero) || this.docNativeHasValue
    },
  },
  watch: {
    numero(value) {
      this.docNativeHasValue = Boolean(value)
    },
    tipoId: {
      handler(id) {
        if (!this.initialized || id == null) return
        const row = this.tiposDoc.find((t) => t.id === id)
        if (row) this.$emit('tipo-change', row)
      },
    },
    loadingTipos(val) {
      if (!val) {
        this.$nextTick(() => this.setupDocAutofillSync())
      }
    },
  },
  async mounted() {
    await this.fetchTiposDocumentos()
    if (!this.tiposDoc.length) {
      this.initialized = true
      this.$nextTick(() => this.setupDocAutofillSync())
      return
    }
    let id = this.tipoId
    if (id == null) {
      const dni = this.tiposDoc.find((t) => t.id === 1)
      id = dni ? dni.id : this.tiposDoc[0].id
      this.$emit('update:tipoId', id)
      const row = this.tiposDoc.find((t) => t.id === id)
      if (row) this.$emit('tipo-change', row)
    } else {
      const row = this.tiposDoc.find((t) => t.id === id)
      if (row) this.$emit('tipo-change', row)
    }
    this.initialized = true
    this.$nextTick(() => {
      this.$refs.docInput?.resetValidation?.()
      this.setupDocAutofillSync()
    })
  },
  beforeUnmount() {
    this.teardownDocAutofillSync()
  },
  methods: {
    ...mapActions({
      fetchTiposDocumentos: 'taxonomy/fetchTiposDocumentos',
    }),
    getDocNativeInput() {
      return this.$refs.docInput?.$el?.querySelector?.('input') ?? null
    },
    syncFromDomAutofill() {
      const input = this.getDocNativeInput()
      this.docNativeHasValue = Boolean(input?.value)
      if (!input?.value) return
      const domVal = String(input.value)
      const modelVal = String(this.numero ?? '')
      if (domVal === modelVal) return
      this.onUpdateModel(domVal)
      this.$nextTick(() => this.$refs.docInput?.resetValidation?.())
    },
    teardownDocAutofillSync() {
      this.docAutofillTimeouts.forEach(clearTimeout)
      this.docAutofillTimeouts = []
      const input = this.getDocNativeInput()
      if (input && this.docAutofillOnAnim) {
        input.removeEventListener('animationstart', this.docAutofillOnAnim)
      }
      this.docAutofillOnAnim = null
    },
    setupDocAutofillSync() {
      if (this.disabled || this.loadingTipos) return
      this.teardownDocAutofillSync()
      const input = this.getDocNativeInput()
      if (input) {
        this.docAutofillOnAnim = (e) => {
          if (e.animationName === 'portal-doc-autofill-start') {
            this.syncFromDomAutofill()
          }
        }
        input.addEventListener('animationstart', this.docAutofillOnAnim)
      }
      const delays = [0, 50, 100, 200, 400, 800, 1600]
      delays.forEach((ms) => {
        this.docAutofillTimeouts.push(
          setTimeout(() => this.syncFromDomAutofill(), ms)
        )
      })
    },
    selectTipo(item) {
      if (this.readonly || this.disabled || !item) return
      this.menuOpen = false
      this.$emit('update:tipoId', item.id)
      this.$emit('update:numero', '')
      this.$emit('tipo-change', item)
      this.$nextTick(() => this.$refs.docInput?.resetValidation?.())
    },
    onUpdateModel(val) {
      const raw = val == null ? '' : String(val)
      const next = this.hasMascara
        ? formatWithMascara(this.selectedMascara, raw)
        : raw.replace(/[^\x20-\x7E]/g, '').slice(0, this.documentoMaxLength)
      this.$emit('update:numero', next)
    },
    onBlur() {
      const tipo = this.selectedTipo
      if (!tipo || !this.numero) return

      if (tipo.mascara) {
        const formateado = this.formatearDocumentoConMascara(tipo.mascara, this.numero)
        if (formateado !== this.numero) {
          this.$emit('update:numero', formateado)
          this.$nextTick(() => this.$refs.docInput?.validate?.())
        }
        if (!mascaraIsOnlyDigits(tipo.mascara)) return
      }

      if (tipo.completarConCeros && tipo.cantidadCeros > 0) {
        const n = String(this.numero)
        const faltan = tipo.cantidadCeros - n.length
        if (faltan > 0) {
          this.$emit('update:numero', '0'.repeat(faltan) + n)
          this.$nextTick(() => this.$refs.docInput?.validate?.())
        }
      }
    },
    formatearDocumentoConMascara(mascara, valor) {
      if (!mascara || !valor) return valor
      return formatWithMascara(mascara, valor)
    },
    onPaste(event) {
      if (this.readonly || this.disabled || this.loadingTipos) return
      event.preventDefault()
      const text =
        event.clipboardData?.getData?.('text/plain')
        ?? (typeof window !== 'undefined'
          ? window.clipboardData?.getData?.('Text')
          : '')
          ?? ''
      this.onUpdateModel(text)
      this.$nextTick(() => this.$refs.docInput?.resetValidation?.())
    },
    onKeydown(event) {
      const key = event.key ?? ''
      const allowedKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ]
      if (event.ctrlKey || event.altKey || event.metaKey) {
        const ok = ['a', 'c', 'v', 'x', 'z', 'y']
        if (!ok.includes(key.toLowerCase())) event.preventDefault()
        return
      }
      if (allowedKeys.includes(key) || key.length !== 1) return

      if (this.hasMascara) {
        if (!/^[\x20-\x7E]$/.test(key)) {
          event.preventDefault()
          return
        }
        const input = this.getDocNativeInput()
        const current = String(input?.value ?? this.numero ?? '')
        const start = input?.selectionStart ?? current.length
        const end = input?.selectionEnd ?? current.length
        const nextRaw = `${current.slice(0, start)}${key}${current.slice(end)}`
        const currentFormatted = formatWithMascara(this.selectedMascara, current)
        const nextFormatted = formatWithMascara(this.selectedMascara, nextRaw)
        if (nextFormatted === currentFormatted) {
          event.preventDefault()
        }
        return
      }

      if (!/^[\x20-\x7E]$/.test(key)) {
        event.preventDefault()
        return
      }
    },
  },
}
</script>

<style scoped>
.btn-tipo-doc {
  font-weight: 700;
  color: var(--portal-primary, rgb(var(--v-theme-primary)));
  text-transform: none;
  letter-spacing: normal;
}

.btn-tipo-doc :deep(.v-btn__content) {
  font-weight: 700;
}

/** Tipo de documento fijo (readonly / disabled): sin opacidad de v-btn--disabled. */
.tipo-doc-tipo-fijo {
  font-weight: 700;
  color: #424242 !important;
  letter-spacing: normal;
  line-height: 1.25;
  opacity: 1 !important;
}

.tipo-doc-field :deep(.v-field--disabled) .tipo-doc-tipo-fijo {
  opacity: 0.92 !important;
}

.tipo-doc-field :deep(.v-field--readonly) .tipo-doc-tipo-fijo {
  opacity: 1 !important;
}

.tipo-doc-field--dark :deep(.tipo-doc-tipo-fijo) {
  color: rgba(var(--v-theme-on-surface), 0.95) !important;
}

.tipo-doc-field :deep(.v-field--readonly .v-field__prepend-inner),
.tipo-doc-field :deep(.v-field--disabled .v-field__prepend-inner) {
  opacity: 1 !important;
}

.tipo-doc-prepend {
  height: 100%;
  margin-inline-end: 0;
  margin-inline-start: -8px;
}
.tipo-doc-divider {
  height: 60%;
  align-self: center;
  border-color: rgba(0, 0, 0, 0.22) !important;
  opacity: 1;
}
.tipo-doc-field--dark :deep(.tipo-doc-divider) {
  border-color: rgba(var(--v-theme-on-surface), 0.55) !important;
}
.tipo-doc-field :deep(.v-field__prepend-inner) {
  padding-top: 0;
  align-items: center;
}
:deep(input:-webkit-autofill) {
  animation-name: portal-doc-autofill-start;
  animation-duration: 0.001s;
}
@keyframes portal-doc-autofill-start {
  from {
    opacity: 1;
  }
  to {
    opacity: 1;
  }
}
</style>
